import { type ComponentProps } from "react";
import { Slot } from "@radix-ui/react-slot";
import { cva, type VariantProps } from "class-variance-authority";

import { cn } from "@/lib/utils";

const buttonVariants = cva(
  `
    inline-flex items-center justify-center gap-2 rounded-md text-sm font-medium
    whitespace-nowrap transition-colors
    focus-visible:ring-1 focus-visible:ring-ring focus-visible:outline-none
    disabled:pointer-events-none disabled:opacity-25
    [&_svg]:pointer-events-none [&_svg]:size-4 [&_svg]:shrink-0
  `,
  {
    variants: {
      variant: {
        default: `
          bg-primary text-primary-foreground shadow-sm
          hover:bg-primary/90
        `,
        destructive: `
          bg-destructive shadow-sm
          hover:bg-destructive/90
        `,
        outline: `
          border border-input bg-background shadow-sm
          hover:bg-accent hover:text-accent-foreground
        `,
        secondary: `
          bg-secondary text-secondary-foreground shadow-sm
          hover:bg-secondary/80
        `,
        ghost: `hover:bg-accent hover:text-accent-foreground`,
        consoleSurface: `
          border border-(--console-border) bg-background shadow-none
          hover:bg-accent hover:text-accent-foreground
        `,
        contextPill: `
          rounded-(--console-pill-radius) border border-(--console-border-input)
          bg-(--console-pill-bg) text-(--console-pill-fg)
          hover:bg-(--console-pill-bg-hover) hover:text-(--console-pill-fg)
          focus-visible:ring-0
        `,
        link: `
          text-primary underline-offset-4
          hover:underline
        `,
      },
      size: {
        default: "h-9 px-4 py-2",
        sm: "h-8 rounded-md px-3 text-xs",
        contextPill: "h-auto px-3.5 py-[5px] text-[12.5px] font-medium",
        lg: "h-10 rounded-md px-8",
        icon: "size-9",
      },
    },
    defaultVariants: {
      variant: "default",
      size: "default",
    },
  },
);

export type ButtonProps = ComponentProps<"button"> &
  VariantProps<typeof buttonVariants> & {
    asChild?: boolean;
  };

const Button = ({
  className,
  variant,
  size,
  asChild = false,
  ref,
  ...props
}: ButtonProps) => {
  const Comp = asChild ? Slot : "button";
  return (
    <Comp
      className={cn(buttonVariants({ variant, size, className }))}
      ref={ref}
      {...props}
    />
  );
};
Button.displayName = "Button";

export { Button };
